import { SyntheticEvent } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { CarouselCard } from '@/shared/types/action';

import Card from './Card';

type CarouselSliderProps = {
  cards: CarouselCard[];
  activeCard: number;
  navigateSlider: (e: SyntheticEvent, direction: 'previous' | 'next') => void;
  isDragging?: boolean;
  handlePaginationClick?: (e: SyntheticEvent, index: number) => void;
};

const ARROW_BTN =
  'absolute top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded bg-white/50 p-0 disabled:hidden focus-visible:ring-2 focus-visible:ring-stroke';

const CarouselSlider = ({
  activeCard,
  cards,
  navigateSlider,
  isDragging = false,
  handlePaginationClick,
}: CarouselSliderProps) => {
  const { t } = useTranslation();
  // When you have deleted all cards
  if (cards.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative aspect-video w-full overflow-hidden',
        isDragging && 'border border-transparent'
      )}
    >
      <Card media={cards[activeCard]?.media} />

      {/* Left arrow */}
      <button
        className={cn(ARROW_BTN, 'left-2')}
        type="button"
        aria-label={t('dialog.carousel.previous_slide')}
        onClick={(e) => navigateSlider(e, 'previous')}
        disabled={activeCard === 0}
      >
        <ChevronLeftIcon className="text-foreground-muted" size={18} />
      </button>

      {/* Right arrow */}
      <button
        className={cn(ARROW_BTN, 'right-2')}
        type="button"
        aria-label={t('dialog.carousel.next_slide')}
        onClick={(e) => navigateSlider(e, 'next')}
        disabled={activeCard === cards.length - 1}
        data-testid="carousel-navigate-right"
      >
        <ChevronRightIcon className="text-foreground-muted" size={18} />
      </button>

      {/* Pagination dots — only shown for multiple cards */}
      {cards.length > 1 && (
        <div className="absolute bottom-0 flex h-6 w-full items-center justify-center gap-1 bg-[rgb(217_217_217/0.4)]">
          {cards.map((card, idx) => (
            <button
              type="button"
              data-testid="carousel-dot"
              key={`${card?.media?.url ?? ''}-${card?.title ?? ''}-${idx}`}
              onClick={(e) => handlePaginationClick?.(e, idx)}
              className={cn(
                'h-1 w-6 bg-bg-muted focus-visible:ring-2 focus-visible:ring-stroke',
                activeCard === idx && 'bg-foreground-muted'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CarouselSlider;
